import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';

import useMailerDriver from 'src/modules/mailer/hooks/useMailerDriver';
import useMailboxService from 'src/modules/mailer/hooks/useMailboxService';
import {
    MAILBOX_CASE_THREAD_QUERY_KEY,
    buildCaseReplyPayload,
    buildMailboxReplySubject,
    getMessageId,
    getMessageSubject,
    isValidMailboxEmail,
    mapMailboxActionError,
    normalizeCaseThreadResponse,
    parseMailboxAddressList,
    stripMailboxHtml,
} from '../../../helpers';

const SENT_FOLDER = 'SENT';
const EMPTY_ITEMS = [];

const useCaseMailboxThread = ({
    t,
    caseId,
    report,
    prefillHtml = null,
    onPrefillConsumed,
    onSent,
}) => {
    const service = useMailboxService();
    const { active } = useMailerDriver();
    const queryClient = useQueryClient();
    const [formKey, setFormKey] = useState(0);
    const [prefillBody, setPrefillBody] = useState('');
    const queryKey = [MAILBOX_CASE_THREAD_QUERY_KEY, active, caseId];

    const {
        data,
        isLoading,
        isFetching,
    } = useQuery({
        queryKey,
        queryFn: async () => {
            const payload = await service.fetchCaseThread(caseId);

            return normalizeCaseThreadResponse(payload);
        },
        enabled: Boolean(caseId),
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') {
                console.error(error);
            }

            toast.error(mapMailboxActionError(
                error,
                t,
                'An error occurred while loading emails.'
            ));
        },
    });

    const items = data?.items ?? EMPTY_ITEMS;
    const requesterEmail = data?.requesterEmail || report?.email || '';

    const lastInbound = useMemo(() => {
        const inbound = [...items].reverse().find((item) => {
            const folder = String(
                item?.folder || item?.imap_folder || item?.imapFolder || ''
            ).toUpperCase();

            return folder !== SENT_FOLDER;
        });

        return inbound || items[items.length - 1] || null;
    }, [items]);

    useEffect(() => {
        if (!prefillHtml) {
            return;
        }

        setPrefillBody(stripMailboxHtml(prefillHtml) || prefillHtml);
        setFormKey((current) => current + 1);
        onPrefillConsumed?.();
    }, [prefillHtml, onPrefillConsumed]);

    const sendMutation = useMutation({
        mutationFn: async (payload) => service.replyCase(caseId, payload),
        onSuccess: async () => {
            toast.success(t('Email sent successfully.'));
            setPrefillBody('');
            setFormKey((current) => current + 1);
            await queryClient.invalidateQueries(queryKey);
            onSent?.();
        },
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') {
                console.error(error);
            }

            toast.error(mapMailboxActionError(
                error,
                t,
                'An error occurred while sending the email.'
            ));
        },
    });

    const handleSubmit = (_event, values) => {
        const to = parseMailboxAddressList(values?.to || requesterEmail);

        if (!to.length || !to.every(isValidMailboxEmail)) {
            toast.error(t('Please enter valid email addresses, separated by commas.'));
            return;
        }

        sendMutation.mutate(buildCaseReplyPayload({
            to,
            subject: values?.subject,
            body: values?.body,
            parentId: getMessageId(lastInbound),
        }));
    };

    return {
        items,
        requesterEmail,
        defaultSubject: lastInbound
            ? buildMailboxReplySubject(getMessageSubject(lastInbound))
            : '',
        formKey,
        prefillBody,
        isLoading: isLoading || (isFetching && !data),
        isSending: sendMutation.isLoading,
        handleSubmit,
    };
};

export default useCaseMailboxThread;
